import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import PanToolAltOutlinedIcon from "@mui/icons-material/PanToolAltOutlined";

interface SelectItemPromptProps {
  itemName?: string;
  message?: string;

  minWidth?: number;
  maxWidth?: number;
  minHeight?: number;
  maxHeight?: number;
}

const BASE_WIDTH = 280;
const BASE_HEIGHT = 160;

export default function SelectItemPrompt({
  itemName = "item",
  message,
  minWidth = 120,
  maxWidth = 360,
  minHeight = 80,
  maxHeight = 240,
}: SelectItemPromptProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);

  const [availableSize, setAvailableSize] = useState({
    width: maxWidth,
    height: maxHeight,
  });

  const displayMessage =
    message ?? `Select a ${itemName} to view details`;

  useEffect(() => {
    const container = containerRef.current;
    const parent = container?.parentElement;

    if (!parent) {
      return;
    }

    const updateSize = () => {
      const parentRect = parent.getBoundingClientRect();

      setAvailableSize({
        width: parentRect.width,
        height: parentRect.height,
      });
    };

    updateSize();

    const resizeObserver = new ResizeObserver(updateSize);
    resizeObserver.observe(parent);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const dimensions = useMemo(() => {
    const boundedWidth = Math.min(
      maxWidth,
      Math.max(minWidth, availableSize.width)
    );

    const boundedHeight = Math.min(
      maxHeight,
      Math.max(minHeight, availableSize.height)
    );

    const widthScale = boundedWidth / BASE_WIDTH;
    const heightScale = boundedHeight / BASE_HEIGHT;
    const scale = Math.min(widthScale, heightScale);

    return {
      scale,
      width: BASE_WIDTH * scale,
      height: BASE_HEIGHT * scale,
    };
  }, [
    availableSize,
    minWidth,
    maxWidth,
    minHeight,
    maxHeight,
  ]);

  const { scale, width, height } = dimensions;

  return (
    <Box
      ref={containerRef}
      role="status"
      aria-label={displayMessage}
      sx={{
        width: "100%",
        height: "100%",
        minWidth: 0,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        overflow: "hidden",
        textAlign: "center",
      }}
    >
      <Box
        sx={{
          width,
          height,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-start",
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: 90 * scale,
            height: 90 * scale,
            mb: `${16 * scale}px`,
            color: "text.secondary",
          }}
        >
          <PanToolAltOutlinedIcon
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: 82 * scale,
              height: 82 * scale,
            }}
          />

          <Box
            sx={{
              position: "absolute",
              right: -2 * scale,
              bottom: -2 * scale,
              width: 46 * scale,
              height: 46 * scale,
              borderRadius: "50%",
              bgcolor: "background.paper",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <DescriptionOutlinedIcon
              sx={{
                width: 38 * scale,
                height: 38 * scale,
              }}
            />
          </Box>
        </Box>

        <Typography
          component="p"
          sx={{
            maxWidth: `${260 * scale}px`,
            fontSize: `${20 * scale}px`,
            lineHeight: 1.25,
            fontWeight: 600,
            color: "text.secondary",
            overflowWrap: "break-word",
          }}
        >
          {displayMessage}
        </Typography>
      </Box>
    </Box>
  );
}

